(function () {
  'use strict';

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var activeMobileMenuRoot = null;
  var mobileMenuScrollY = 0;
  var mobileMenuUnlockFrame;

  function finishMobileMenuUnlock(restoreY) {
    if (activeMobileMenuRoot) return;
    window.cancelAnimationFrame(mobileMenuUnlockFrame);
    window.scrollTo(0, restoreY);
    mobileMenuUnlockFrame = window.requestAnimationFrame(function () {
      if (activeMobileMenuRoot) return;
      document.documentElement.classList.remove('solvex-menu-open', 'solvex-menu-scroll-locked', 'solvex-menu-restoring');
      document.documentElement.style.removeProperty('--solvex-menu-scroll-y');
      window.scrollTo(0, restoreY);
      mobileMenuUnlockFrame = undefined;
    });
  }

  function lockPageForMobileMenu() {
    window.cancelAnimationFrame(mobileMenuUnlockFrame);
    mobileMenuUnlockFrame = undefined;
    mobileMenuScrollY = Math.max(0, window.scrollY || window.pageYOffset || 0);
    document.documentElement.style.setProperty('--solvex-menu-scroll-y', -mobileMenuScrollY + 'px');
    document.documentElement.classList.remove('solvex-menu-restoring');
    document.documentElement.classList.add('solvex-menu-open', 'solvex-menu-scroll-locked');
  }

  function unlockPageForMobileMenu() {
    var restoreY = mobileMenuScrollY;
    document.documentElement.classList.remove('solvex-menu-open', 'solvex-menu-scroll-locked');
    document.documentElement.classList.add('solvex-menu-restoring');
    document.documentElement.style.removeProperty('--solvex-menu-scroll-y');
    mobileMenuUnlockFrame = window.requestAnimationFrame(function () {
      if (activeMobileMenuRoot) return;
      window.scrollTo(0, restoreY);
      mobileMenuUnlockFrame = window.requestAnimationFrame(function () { finishMobileMenuUnlock(restoreY); });
    });
  }

  function initInputModality() {
    if (document.documentElement.dataset.solvexInputReady === 'true') return;
    document.documentElement.dataset.solvexInputReady = 'true';
    document.addEventListener('pointerdown', function () {
      document.documentElement.dataset.solvexInput = 'pointer';
    }, true);
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Tab' || event.key === 'Enter' || event.key === ' ' || event.key.indexOf('Arrow') === 0) {
        document.documentElement.dataset.solvexInput = 'keyboard';
      }
    }, true);
  }

  function initSlider(root) {
    if (root.dataset.solvexReady === 'true') return;
    var slides = Array.from(root.querySelectorAll('[data-solvex-slide]'));
    var dots = Array.from(root.querySelectorAll('[data-solvex-dot]'));
    if (!slides.length) return;

    root.dataset.solvexReady = 'true';
    var current = Math.max(0, slides.findIndex(function (slide) { return slide.classList.contains('is-active'); }));
    var paused = reducedMotion || root.dataset.autoplay !== 'true';
    var speed = Number(root.dataset.speed) || 6000;
    var timer;
    var transitionTimer;
    var animating = false;
    var queuedRequest = null;
    var drag = null;
    var suppressClick = false;
    var wheelTotal = 0;
    var wheelTimer;

    function splitLetters(element, elementIndex) {
      if (element.dataset.solvexLettersReady === 'true') return;
      var words = element.textContent.split(/(\s+)/);
      var characterIndex = 0;
      var wordIndex = 0;
      var lineElement = document.createElement('span');
      lineElement.className = 'solvex-letter-line';
      element.textContent = '';
      words.forEach(function (word) {
        if (/^\s+$/.test(word)) {
          lineElement.appendChild(document.createTextNode(word));
          return;
        }
        var wordElement = document.createElement('span');
        wordElement.className = 'solvex-letter-word';
        wordElement.style.setProperty('--word-order', wordIndex);
        wordElement.style.setProperty('--copy-order', elementIndex);
        Array.from(word).forEach(function (character) {
          var letter = document.createElement('span');
          letter.className = 'solvex-letter';
          letter.style.setProperty('--letter-order', characterIndex);
          letter.style.setProperty('--copy-order', elementIndex);
          letter.textContent = character;
          wordElement.appendChild(letter);
          characterIndex += 1;
        });
        lineElement.appendChild(wordElement);
        wordIndex += 1;
      });
      element.appendChild(lineElement);
      element.dataset.solvexLettersReady = 'true';
    }

    slides.forEach(function (slide) {
      slide.querySelectorAll('[data-solvex-letter-text]').forEach(splitLetters);
    });

    function updateDots() {
      dots.forEach(function (dot, dotIndex) {
        dot.setAttribute('aria-selected', String(dotIndex === current));
        dot.tabIndex = dotIndex === current ? 0 : -1;
      });
    }

    function settleSlides() {
      window.clearTimeout(transitionTimer);
      slides.forEach(function (slide, slideIndex) {
        var active = slideIndex === current;
        slide.classList.toggle('is-active', active);
        slide.classList.remove('is-arriving', 'is-leaving', 'is-drag-preview');
        slide.removeAttribute('data-direction');
        slide.style.removeProperty('opacity');
        slide.style.removeProperty('transform');
        slide.setAttribute('aria-hidden', String(!active));
        slide.inert = !active;
      });
      animating = false;
      root.setAttribute('aria-busy', 'false');
      if (queuedRequest) {
        var request = queuedRequest;
        queuedRequest = null;
        window.requestAnimationFrame(function () {
          show(request.index, request.userInitiated, request.direction);
        });
      }
    }

    function show(index, userInitiated, requestedDirection) {
      var next = (index + slides.length) % slides.length;
      if (next === current) {
        updateDots();
        if (userInitiated) restart();
        return;
      }

      if (animating) {
        queuedRequest = { index: next, userInitiated: userInitiated, direction: requestedDirection };
        return;
      }
      var outgoing = slides[current];
      var incoming = slides[next];
      var direction = requestedDirection || (next > current ? 1 : -1);
      if (current === slides.length - 1 && next === 0) direction = 1;
      if (current === 0 && next === slides.length - 1) direction = -1;

      current = next;
      updateDots();
      incoming.classList.add('is-active');
      incoming.setAttribute('aria-hidden', 'false');
      incoming.inert = false;
      outgoing.setAttribute('aria-hidden', 'true');
      outgoing.inert = true;

      if (reducedMotion) {
        settleSlides();
        if (userInitiated) restart();
        return;
      }

      animating = true;
      root.setAttribute('aria-busy', 'true');
      outgoing.dataset.direction = String(direction);
      incoming.dataset.direction = String(direction);
      outgoing.classList.add('is-leaving');
      incoming.classList.add('is-arriving');
      transitionTimer = window.setTimeout(settleSlides, 860);
      if (userInitiated) restart();
    }

    function stop() {
      window.clearInterval(timer);
      timer = undefined;
    }

    function start() {
      stop();
      if (!paused && slides.length > 1) timer = window.setInterval(function () { show(current + 1, false, 1); }, speed);
    }

    function restart() {
      stop();
      start();
    }

    dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        var index = Number(dot.dataset.index);
        show(index, true, index >= current ? 1 : -1);
      });
      dot.addEventListener('keydown', function (event) {
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          var direction = event.key === 'ArrowRight' ? 1 : -1;
          show(current + direction, true, direction);
          dots[current].focus();
        }
      });
    });

    function clearDragPreview() {
      if (!drag) return;
      var outgoing = slides[current];
      var preview = slides[drag.targetIndex];
      outgoing?.style.removeProperty('opacity');
      outgoing?.style.removeProperty('transform');
      preview?.style.removeProperty('opacity');
      preview?.style.removeProperty('transform');
      preview?.classList.remove('is-drag-preview');
      root.classList.remove('is-dragging');
    }

    function cancelDrag() {
      clearDragPreview();
      drag = null;
      start();
    }

    root.addEventListener('pointerdown', function (event) {
      if (slides.length < 2 || animating || event.button > 0 || event.target.closest('a, button, input, select, textarea, summary')) return;
      drag = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        startTime: performance.now(),
        deltaX: 0,
        horizontal: false,
        targetIndex: current,
        direction: 1,
      };
      root.setPointerCapture?.(event.pointerId);
      stop();
    });

    root.addEventListener('pointermove', function (event) {
      if (!drag || event.pointerId !== drag.pointerId) return;
      var deltaX = event.clientX - drag.startX;
      var deltaY = event.clientY - drag.startY;
      if (!drag.horizontal) {
        if (Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) > 10) {
          cancelDrag();
          return;
        }
        if (Math.abs(deltaX) < 8) return;
        drag.horizontal = true;
        root.classList.add('is-dragging');
      }

      event.preventDefault();
      drag.deltaX = deltaX;
      drag.direction = deltaX < 0 ? 1 : -1;
      drag.targetIndex = (current + drag.direction + slides.length) % slides.length;
      var progress = Math.min(1, Math.abs(deltaX) / Math.max(120, root.clientWidth * 0.45));
      var outgoing = slides[current];
      var preview = slides[drag.targetIndex];

      slides.forEach(function (slide, index) {
        if (index !== current && index !== drag.targetIndex) {
          slide.classList.remove('is-drag-preview');
          slide.style.removeProperty('opacity');
          slide.style.removeProperty('transform');
        }
      });
      preview.classList.add('is-drag-preview');
      outgoing.style.opacity = String(1 - progress * 0.72);
      outgoing.style.transform = 'translate3d(' + (deltaX * 0.055) + 'px,0,0) scale(' + (1 + progress * 0.018) + ')';
      preview.style.opacity = String(progress * 0.9);
      preview.style.transform = 'translate3d(' + (-drag.direction * (1 - progress) * 18) + 'px,0,0) scale(' + (1.025 - progress * 0.025) + ')';
    }, { passive: false });

    function finishDrag(event) {
      if (!drag || event.pointerId !== drag.pointerId) return;
      var completedDrag = drag;
      var elapsed = Math.max(1, performance.now() - completedDrag.startTime);
      var velocity = Math.abs(completedDrag.deltaX) / elapsed;
      var shouldMove = completedDrag.horizontal && (Math.abs(completedDrag.deltaX) > Math.min(72, root.clientWidth * 0.18) || velocity > 0.45);
      suppressClick = completedDrag.horizontal && Math.abs(completedDrag.deltaX) > 10;
      clearDragPreview();
      drag = null;
      if (shouldMove) show(completedDrag.targetIndex, true, completedDrag.direction);
      else start();
      window.setTimeout(function () { suppressClick = false; }, 0);
    }

    root.addEventListener('pointerup', finishDrag);
    root.addEventListener('pointercancel', cancelDrag);
    root.addEventListener('click', function (event) {
      if (suppressClick) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (slides.length < 2 || event.defaultPrevented || !(event.target instanceof Element)) return;
      if (event.target.closest('a, button, input, select, textarea, summary, [role="button"], [role="link"]')) return;
      var bounds = root.getBoundingClientRect();
      var direction = event.clientX < bounds.left + bounds.width / 2 ? -1 : 1;
      show(current + direction, true, direction);
    }, true);

    root.addEventListener('keydown', function (event) {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      if (event.target !== root && event.target.closest?.('a, button, input, select, textarea, summary')) return;
      event.preventDefault();
      var direction = event.key === 'ArrowRight' ? 1 : -1;
      show(current + direction, true, direction);
    });

    var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
    var cursorHint = document.createElement('span');
    cursorHint.className = 'solvex-hero__cursor-hint';
    cursorHint.setAttribute('aria-hidden', 'true');
    root.appendChild(cursorHint);

    root.addEventListener('pointermove', function (event) {
      if (!finePointer.matches || drag?.horizontal || !(event.target instanceof Element) || event.target.closest('a, button, input, select, textarea, summary, [role="button"], [role="link"]')) {
        cursorHint.classList.remove('is-visible');
        return;
      }
      var bounds = root.getBoundingClientRect();
      var previous = event.clientX < bounds.left + bounds.width / 2;
      cursorHint.textContent = previous ? '← PREV' : 'NEXT →';
      cursorHint.style.setProperty('--cursor-x', (event.clientX - bounds.left) + 'px');
      cursorHint.style.setProperty('--cursor-y', (event.clientY - bounds.top) + 'px');
      cursorHint.classList.add('is-visible');
    });
    root.addEventListener('pointerleave', function () { cursorHint.classList.remove('is-visible'); });

    root.addEventListener('wheel', function (event) {
      if (slides.length < 2 || animating || Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
      wheelTotal += event.deltaX;
      window.clearTimeout(wheelTimer);
      wheelTimer = window.setTimeout(function () { wheelTotal = 0; }, 140);
      if (Math.abs(wheelTotal) < 45) return;
      event.preventDefault();
      var direction = wheelTotal > 0 ? 1 : -1;
      wheelTotal = 0;
      show(current + direction, true, direction);
    }, { passive: false });

    root.addEventListener('mouseenter', stop);
    root.addEventListener('mouseleave', start);
    root.addEventListener('focusin', stop);
    root.addEventListener('focusout', start);
    document.addEventListener('visibilitychange', function () { if (document.hidden) stop(); else start(); });
    settleSlides();
    updateDots();
    root.solvexShowSlide = show;
    start();
  }

  function initSearch() {
    document.querySelectorAll('[data-solvex-search]').forEach(function (details) {
      if (details.dataset.searchReady) return;
      details.dataset.searchReady = 'true';
      var close = details.querySelector('[data-solvex-search-close]');
      var input = details.querySelector('input[type="search"]');
      var overlay = details.querySelector('.solvex-search__overlay');
      var summary = details.querySelector('summary');
      details.addEventListener('toggle', function () {
        summary?.setAttribute('aria-expanded', String(details.open));
        document.documentElement.classList.toggle('solvex-search-open', details.open);
        if (details.open) {
          document.querySelector('#site-settings')?.removeAttribute('open');
          if (input) window.setTimeout(function () { input.focus(); }, 50);

          // The header can still be mid-transition from the scroll hide/reveal
          // transform (site-preferences.js) when this opens. A non-'none'
          // transform on that ancestor becomes the containing block for this
          // overlay's position:fixed, so it renders offset instead of
          // covering the viewport and the page is left stuck (scroll locked,
          // nothing visible). Remove the hidden class and cut the transition
          // short so the transform is back to none before the overlay's
          // position is computed, same fix already used for the mobile menu.
          var headerSection = details.closest('.solvex-header-section');
          if (headerSection) {
            headerSection.classList.remove('solvex-header-section--hidden');
            var previousTransition = headerSection.style.transition;
            headerSection.style.transition = 'none';
            headerSection.getBoundingClientRect();
            headerSection.style.transition = previousTransition;
          }
        }
      });
      if (close) close.addEventListener('click', function () { details.open = false; });
      if (overlay) overlay.addEventListener('click', function (event) { if (event.target === overlay) details.open = false; });
      details.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') {
          // Open live results close first; predictive-search.js handles that on keyup.
          if (event.target.closest?.('predictive-search[open]')) return;
          details.open = false;
          summary?.focus();
          return;
        }
        if (event.key !== 'Tab' || !details.open || !overlay) return;
        var focusable = Array.from(overlay.querySelectorAll('input, button, a[href], [tabindex]:not([tabindex="-1"])')).filter(function (element) { return !element.disabled; });
        if (!focusable.length) return;
        var first = focusable[0];
        var last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });
    });
  }

  function initMobileMenus() {
    document.querySelectorAll('[data-solvex-mobile-menu]').forEach(function (root) {
      if (root.dataset.menuReady === 'true') return;

      var toggle = root.querySelector('[data-solvex-menu-toggle]');
      var panel = root.querySelector('[data-solvex-menu-panel]');
      var submenuDetails = Array.from(root.querySelectorAll('[data-solvex-mobile-submenu]'));
      var countrySelect = root.querySelector('[data-solvex-mobile-country]');
      var mobileCart = root.querySelector('[data-solvex-mobile-cart]');
      var cartCount = root.querySelector('[data-solvex-mobile-cart-count]');
      var countryFlag = root.querySelector('[data-country-flag]');
      var searchForm = root.querySelector('.solvex-mobile-drawer__search');
      var searchInput = searchForm?.querySelector('input[type="search"]');
      var lastFocused;
      var hideTimer;
      var isOpen = false;

      if (!toggle || !panel) return;
      root.dataset.menuReady = 'true';
      panel.inert = true;
      panel.setAttribute('aria-hidden', 'true');

      function getHeaderCart() {
        return root.closest('.solvex-header')?.querySelector('#cart-icon-bubble') || document.querySelector('#cart-icon-bubble');
      }

      function setCountryFlag() {
        if (!countryFlag) return;
        var countryCode = countryFlag.dataset.countryCode || '';
        countryFlag.textContent = countryCode.toUpperCase().replace(/[A-Z]/g, function (character) {
          return String.fromCodePoint(127397 + character.charCodeAt(0));
        });
      }

      function syncCart() {
        var mainCart = getHeaderCart();
        var mainCount = mainCart?.querySelector('.sports-cart-count');
        if (!mainCount || !cartCount) return;
        var count = mainCount.textContent.trim();
        cartCount.textContent = count;
        var accessibleCartText = mainCart.querySelector('.visually-hidden')?.textContent.trim();
        mobileCart?.setAttribute('aria-label', accessibleCartText || ('Bag, ' + count + ' items'));
      }

      function focusableElements() {
        return [toggle].concat(Array.from(panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'))).filter(function (element) {
          return !element.hidden && element.getClientRects().length > 0 && !element.closest('[inert]');
        });
      }

      /*
        While the drawer is open, everything except the toggle (the close ×)
        and the drawer itself is inert: the header search icon beside the
        toggle, the logo, the header cart and the page behind. Before this,
        Tab went from the × straight to those hidden controls (audit A11Y-02).
        Walks up from both kept elements and makes every other branch inert.
        Only what this made inert is released on close.
      */
      var inertOutside = [];
      function makeOutsideInert() {
        var keep = [toggle, panel];
        var path = new Set();
        keep.forEach(function (element) {
          for (var node = element; node && node !== document.documentElement; node = node.parentElement) path.add(node);
        });
        path.forEach(function (node) {
          if (keep.indexOf(node) !== -1) return;
          Array.from(node.children).forEach(function (child) {
            if (path.has(child) || child.inert) return;
            if (/^(SCRIPT|STYLE|LINK|TEMPLATE|NOSCRIPT)$/.test(child.tagName)) return;
            child.inert = true;
            inertOutside.push(child);
          });
        });
      }

      function releaseOutside() {
        inertOutside.forEach(function (element) { element.inert = false; });
        inertOutside = [];
      }

      function openMenu(openedWithKeyboard) {
        if (isOpen) return;
        if (activeMobileMenuRoot && activeMobileMenuRoot !== root) activeMobileMenuRoot.solvexCloseMenu?.(false);
        window.clearTimeout(hideTimer);
        isOpen = true;
        activeMobileMenuRoot = root;
        lastFocused = document.activeElement;
        document.querySelector('#site-settings')?.removeAttribute('open');
        lockPageForMobileMenu();
        root.closest('.solvex-header-section')?.classList.remove('solvex-header-section--hidden');
        panel.hidden = false;
        panel.inert = false;
        makeOutsideInert();
        panel.setAttribute('aria-hidden', 'false');
        toggle.setAttribute('aria-expanded', 'true');
        toggle.setAttribute('aria-label', 'Close navigation');
        syncCart();
        window.requestAnimationFrame(function () {
          if (!isOpen) return;
          panel.classList.add('is-open');
          if (openedWithKeyboard) {
            root._solvexFocusTimer = window.setTimeout(function () { toggle.focus(); }, reducedMotion ? 0 : 180);
          }
        });
      }

      function closeMenu(returnFocus) {
        window.clearTimeout(hideTimer);
        window.clearTimeout(root._solvexFocusTimer);
        isOpen = false;
        panel.classList.remove('is-open');
        panel.inert = true;
        releaseOutside();
        panel.setAttribute('aria-hidden', 'true');
        toggle.setAttribute('aria-expanded', 'false');
        toggle.setAttribute('aria-label', 'Open navigation');
        if (activeMobileMenuRoot === root) {
          activeMobileMenuRoot = null;
          unlockPageForMobileMenu();
        }
        hideTimer = window.setTimeout(function () {
          if (!isOpen) panel.hidden = true;
        }, reducedMotion ? 0 : 320);
        if (returnFocus !== false) {
          var focusTarget = lastFocused || toggle;
          try {
            focusTarget.focus({ preventScroll: true });
          } catch (error) {
            focusTarget.focus();
            window.scrollTo(0, mobileMenuScrollY);
          }
        }
      }

      root.solvexCloseMenu = closeMenu;

      toggle.addEventListener('click', function (event) {
        event.preventDefault();
        if (!isOpen) openMenu(event.detail === 0);
        else closeMenu(true);
      });

      panel.addEventListener('click', function (event) {
        var link = event.target.closest('a[href]');
        if (!link) return;
        if (link.matches('[data-solvex-mobile-cart]')) {
          event.preventDefault();
          var mainCart = getHeaderCart();
          closeMenu(false);
          if (mainCart) mainCart.click();
          else window.location.assign(link.href);
          return;
        }
        closeMenu(false);
      });

      root.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') {
          event.preventDefault();
          var openPredictiveSearch = event.target.closest?.('predictive-search[open]');
          if (openPredictiveSearch) {
            event.stopPropagation();
            openPredictiveSearch.close?.();
            openPredictiveSearch.querySelector('input[type="search"]')?.focus();
            return;
          }
          closeMenu(true);
          return;
        }
        if (event.key !== 'Tab' || !isOpen) return;
        var focusable = focusableElements();
        if (!focusable.length) return;
        var first = focusable[0];
        var last = focusable[focusable.length - 1];
        // Focus that is somewhere outside the list (it should not be, with
        // the rest inert) is brought back in rather than allowed to walk on.
        var inList = focusable.indexOf(document.activeElement) !== -1;
        if (event.shiftKey && (document.activeElement === first || !inList)) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && (document.activeElement === last || !inList)) {
          event.preventDefault();
          first.focus();
        }
      });

      submenuDetails.forEach(function (submenu) {
        submenu.addEventListener('toggle', function () {
          submenu.querySelector('summary')?.setAttribute('aria-expanded', String(submenu.open));
        });
      });

      if (countrySelect) {
        countrySelect.addEventListener('change', function () {
          countrySelect.form?.submit();
        });
      }

      setCountryFlag();
      syncCart();
      var mainCart = getHeaderCart();
      if (mainCart) {
        root._solvexCartObserver = new MutationObserver(syncCart);
        root._solvexCartObserver.observe(mainCart, { childList: true, subtree: true, characterData: true });
      }

      root._solvexDesktopQuery = window.matchMedia('(min-width: 990px)');
      root._solvexDesktopListener = function (event) {
        if (event.matches) closeMenu(false);
      };
      root._solvexDesktopQuery.addEventListener('change', root._solvexDesktopListener);

      root._solvexMenuCleanup = function () {
        closeMenu(false);
        root._solvexCartObserver?.disconnect();
        if (root._solvexDesktopQuery && root._solvexDesktopListener) {
          root._solvexDesktopQuery.removeEventListener('change', root._solvexDesktopListener);
        }
        window.clearTimeout(root._solvexFocusTimer);
        window.clearTimeout(hideTimer);
      };
    });
  }

  function closeAllMobileMenus() {
    var shouldRestoreScroll = Boolean(activeMobileMenuRoot) || document.documentElement.classList.contains('solvex-menu-scroll-locked');
    document.querySelectorAll('[data-solvex-mobile-menu]').forEach(function (root) {
      root.solvexCloseMenu?.(false);
    });
    activeMobileMenuRoot = null;
    if (shouldRestoreScroll) {
      window.cancelAnimationFrame(mobileMenuUnlockFrame);
      document.documentElement.classList.remove('solvex-menu-open', 'solvex-menu-scroll-locked');
      document.documentElement.classList.add('solvex-menu-restoring');
      document.documentElement.style.removeProperty('--solvex-menu-scroll-y');
      document.body.getBoundingClientRect();
      window.scrollTo(0, mobileMenuScrollY);
    }
    finishMobileMenuUnlock(mobileMenuScrollY);
  }

  function initHistoryControls() {
    if (document.querySelector('[data-solvex-history]') || document.querySelector('main[data-template="index"]')) return;
    var wrapper = document.createElement('nav');
    wrapper.className = 'solvex-history-controls';
    wrapper.dataset.solvexHistory = 'true';
    wrapper.setAttribute('aria-label', 'Page history');
    wrapper.innerHTML = '<button type="button" data-history-back aria-label="Go back"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m15 5-7 7 7 7"/></svg></button><button type="button" data-history-forward aria-label="Go forward"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7"/></svg></button>';
    document.body.appendChild(wrapper);
    var back = wrapper.querySelector('[data-history-back]');
    var forward = wrapper.querySelector('[data-history-forward]');
    back.disabled = history.length <= 1;
    back.addEventListener('click', function () { history.back(); });
    forward.addEventListener('click', function () { history.forward(); });
    requestAnimationFrame(function () { wrapper.classList.add('is-visible'); });
    window.setTimeout(function () { wrapper.classList.remove('is-visible'); }, 5000);
    document.addEventListener('pointermove', function () {
      wrapper.classList.add('is-visible');
      window.clearTimeout(wrapper._hideTimer);
      wrapper._hideTimer = window.setTimeout(function () { wrapper.classList.remove('is-visible'); }, 5000);
    }, { passive: true });
  }

  /*
    Keyboard focus never stops underneath the floating controls (audit
    A11Y-05, WCAG 2.4.11). The accessibility launcher and the history
    buttons are fixed in the bottom-left corner. When a link or field in the
    page takes focus while it is already on screen, the browser does not
    scroll, even with scroll-padding, so it could sit hidden behind them.
    Scroll just far enough to lift it clear. Elements inside fixed layers
    (the popup, the drawer, the controls themselves) are left alone.
  */
  function initFocusClearance() {
    document.addEventListener('focusin', function (event) {
      var target = event.target;
      if (!target || !target.getBoundingClientRect) return;
      if (!target.closest('main, footer')) return;
      var controls = Array.from(document.querySelectorAll('.sx-a11y__launcher, .solvex-history-controls.is-visible'));
      if (!controls.length || controls.some(function (control) { return control.contains(target); })) return;
      var box = target.getBoundingClientRect();
      // Only controls (links, buttons, fields), never a container given focus
      // by script. Focusing #MainContent after the welcome popup closed made
      // the whole page "overlap" the launcher and scrolled it to the bottom
      // (merchant report, 2026-09-28).
      if (target.id === 'MainContent' || box.height > window.innerHeight / 2) return;
      var lift = 0;
      controls.forEach(function (control) {
        var area = control.getBoundingClientRect();
        if (!area.width || !area.height) return;
        var overlaps = box.left < area.right && box.right > area.left && box.top < area.bottom && box.bottom > area.top;
        if (overlaps) lift = Math.max(lift, box.bottom - area.top + 16);
      });
      if (lift > 0) window.scrollBy({ top: lift, left: 0, behavior: 'instant' });
    });
  }

  function init() {
    initInputModality();
    document.querySelectorAll('[data-solvex-slider]').forEach(initSlider);
    initSearch();
    initMobileMenus();
    initHistoryControls();
    initFocusClearance();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  window.addEventListener('pagehide', closeAllMobileMenus);
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) closeAllMobileMenus();
  });
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:block:select', function (event) {
    var slide = event.target.closest?.('[data-solvex-slide]');
    var slider = slide?.closest('[data-solvex-slider]');
    if (!slide || !slider?.solvexShowSlide) return;
    var slides = Array.from(slider.querySelectorAll('[data-solvex-slide]'));
    var index = slides.indexOf(slide);
    var current = slides.findIndex(function (item) { return item.classList.contains('is-active'); });
    slider.solvexShowSlide(index, true, index >= current ? 1 : -1);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    event.target.querySelectorAll('[data-solvex-mobile-menu]').forEach(function (root) {
      root._solvexMenuCleanup?.();
    });
  });
})();
